import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Globe, Award, Handshake } from 'lucide-react';

const values = [
  {
    icon: Users,
    title: 'Family Leadership',
    description: 'Family-owned and operated since 1983. Our leadership brings continuity, long-term thinking, and personal accountability to every partnership.',
  },
  {
    icon: Handshake,
    title: 'Partner First',
    description: 'We build relationships that last. Our success is measured by the success of our brand and retail partners over the long term.',
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'Deep manufacturing relationships across China, Vietnam, Bangladesh, and Egypt give us flexibility and scale.',
  },
  {
    icon: Award,
    title: 'Quality Focus',
    description: 'Consistent quality, on-time delivery, and attention to detail across every program we execute.',
  },
];

const stats = [
  { number: '40+', label: 'Years in Business' },
  { number: '11', label: 'Brand Partners' },
  { number: '4', label: 'Sourcing Regions' },
  { number: '1M+', label: 'Units Annually' },
];

const AboutPage = () => {
  return (
    <main className="bg-[#0a0a0a] min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">About LV1</p>
          <h1 className="text-[48px] md:text-[64px] font-extralight text-white mb-6 tracking-[-0.02em]">
            A Family-Built<br />Footwear Group
          </h1>
          <p className="text-[#8a8a8a] text-[17px] leading-[1.7] max-w-[650px] mx-auto">
            Since 1983, LV1 has been creating brand-driven footwear at scale. We are a family business built on partnerships, quality, and a long-term approach to everything we do.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="p-6 rounded-xl text-center"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <p className="text-[36px] font-extralight text-white mb-2">{stat.number}</p>
              <p className="text-[#6a6a6a] text-[13px] uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Our Story Section - Centered, no photo needed */}
        <div className="mb-20">
          <div className="max-w-[720px] mx-auto text-center">
            <h2 className="text-white text-[32px] font-light mb-10">Our Story</h2>

            {/* Featured quote - refined with subtle background */}
            <div
              className="relative mb-12 py-10 px-8 rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <p className="text-white/75 text-[20px] md:text-[24px] font-extralight leading-[1.6] italic">
                "Built on relationships, driven by quality, focused on the long term."
              </p>
            </div>

            <div className="space-y-5 text-[#8a8a8a] text-[16px] leading-[1.85]">
              <p>
                LV1 began as a family venture in 1983, founded on the belief that strong relationships and quality execution would create lasting value in the footwear industry.
              </p>
              <p>
                Over four decades, we have grown from a small operation into a full-service footwear group, managing brand partnerships and delivering millions of units annually to retailers across North America.
              </p>
              <p>
                What has not changed is our approach: we remain family-led, relationship-focused, and committed to the long-term success of every partner we work with.
              </p>
            </div>

            {/* Timeline link */}
            <div className="mt-10">
              <Link
                to="/history"
                className="inline-flex items-center gap-2 text-white/50 text-[14px] hover:text-white/80 transition-colors duration-300"
              >
                <span>Explore our journey through the decades</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-white text-[32px] font-light mb-4">What Guides Us</h2>
            <p className="text-[#8a8a8a] text-[16px] max-w-[500px] mx-auto">
              The principles that have defined LV1 since the beginning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  className="p-8 rounded-2xl"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mb-5">
                    <Icon size={22} className="text-white/60" />
                  </div>
                  <h3 className="text-white text-[20px] font-medium mb-3">{value.title}</h3>
                  <p className="text-[#8a8a8a] text-[15px] leading-[1.7]">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div
          className="p-10 rounded-2xl text-center"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <h3 className="text-white text-[28px] font-light mb-4">Work With LV1</h3>
          <p className="text-[#8a8a8a] text-[16px] mb-8 max-w-[500px] mx-auto">
            Whether you are a brand looking for a footwear partner or a retailer seeking quality programs, we would like to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-7 py-3.5 bg-white text-[#0a0a0a] rounded-full text-[15px] font-medium hover:bg-white/90 transition-all duration-300"
            >
              Contact Us
            </Link>
            <Link
              to="/brands"
              className="px-7 py-3.5 border border-white/20 text-white rounded-full text-[15px] font-medium hover:bg-white/5 hover:border-white/30 transition-all duration-300"
            >
              View Our Brands
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AboutPage;
